import {Inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {ToastService} from './toast.service';
import {LoadingService} from './loading.service';
import {Order} from '../_models/order';
import {OrderItem} from '../_models/order-item';
import {formattedDate, formatToHoursAndMinutes} from '../_utils/date-utils';
import {Organization} from '../_models/organization';
import {CashSessionSummary} from '../_models/cash-information/cash-session-summary';
import {CashSession} from '../_models/cash-information/cash-session';
import {CurrencyPipe, DatePipe, formatDate} from '@angular/common';
import {buildGiftReceiptHeader, buildLineItem, lineSeparator} from '../_utils/receipt-utils';
import {Timecard} from '../_models/timecard';
import {Availabilities} from '../_constants/availabilities';
import {Tab} from '../_models/tab';
import {LibConfig, LibConfigService} from '../lib.config';
import moment from 'moment';
import {GiftCard} from '../_models/gift-card';
import {WifiPrinter} from '../_models/wifi-printer';
import {WifiPrinterService} from './wifi-printer.service';
import {OrganizationService} from './organization.service';
import {Membership} from '../_models/membership';
import {JavascriptLogService} from './javascript-log.service';

@Injectable({
    providedIn: 'root'
})
export class EpsonPrinterService {
    ePosDevice: any;
    printerDevice: any;
    currentOrganization: Organization;
    currentWifiPrinter: WifiPrinter;
    failedRetry = 0;

    public connected: Observable<string>;
    private connectedSubject: BehaviorSubject<string>;

    constructor(
        @Inject(LibConfigService) private config: LibConfig,
        private wifiPrinterService: WifiPrinterService,
        private javascriptLogService: JavascriptLogService,
        private organizationService: OrganizationService,
        private toastService: ToastService,
        private loadingService: LoadingService,
        private currencyPipe: CurrencyPipe,
        private datePipe: DatePipe
    ) {
        // @ts-ignore
        if (!!window.epson) {
            // @ts-ignore
            this.ePosDevice = new window.epson.ePOSDevice();

            try {
                this.ePosDevice.ondisconnect = () => {
                    this.javascriptLogService.logAction('epson-printer.service', 'ondisconnect', '', true);
                    this.printerDevice = null;
                };
            } catch (e) {
                this.javascriptLogService.logAction('epson-printer.service', 'initialization', JSON.stringify(e), false);
            }
        }

        this.connectedSubject = new BehaviorSubject<string>(!!this.currentWifiPrinter ? 'WIFI' : null);
        this.connected = this.connectedSubject.asObservable();

        this.wifiPrinterService.current.subscribe(w => {
            const previous = this.currentWifiPrinter;
            this.currentWifiPrinter = !!w && w.type === 'EPSON' ? new WifiPrinter(w) : null;

            if (!!this.currentWifiPrinter) {
                this.connectedSubject.next('WIFI');
            }

            if (!!previous
                && !!this.currentWifiPrinter
                && !!this.ePosDevice
                && this.ePosDevice.isConnected()
                && previous.ipAddress !== this.currentWifiPrinter.ipAddress) {
                this.disconnect();
            }

            if (!!this.currentWifiPrinter) {
                this.pulse();
            }
        });
        this.organizationService.current.subscribe(o => this.currentOrganization = !!o ? new Organization(o) : null);

        setInterval(() => {
            this.pulse();
        }, 80000);
    }

    async initializeWifiPrinter(wifiPrinter: WifiPrinter, saveConnection = false) {
        if (!!wifiPrinter) {
            return this.connect(wifiPrinter, 0, true, saveConnection);
        }
    }

    async connect(wifiPrinter: WifiPrinter, retryCount: number, silent: boolean, saveConnection = false) {
        this.javascriptLogService.logAction('epson-printer.service', 'connect', 'start', true);
        if (!silent) {
            this.loadingService.present('Connecting printer.');
        }

        return new Promise((resolve) => {
            try {
                if (saveConnection) {
                    this.currentWifiPrinter = null;
                    this.wifiPrinterService.setCurrent(null);
                }
                this.ePosDevice.connect(wifiPrinter.ipAddress, 8008, async (data) => {
                    if (!silent) {
                        this.loadingService.dismiss();
                    }
                    if (data === 'OK') {
                        console.log(`Connected on ${wifiPrinter.ipAddress}`);
                        this.loadingService.dismiss();
                        if (saveConnection) {
                            this.wifiPrinterService.setCurrent(wifiPrinter);
                            setTimeout(() => this.getPrinterDevice(), 1000);
                        }
                        this.javascriptLogService.logAction('epson-printer.service', 'connect', 'end', true);
                        resolve(true);
                    } else if (data === 'DEVICE_IN_USE' && (!retryCount || retryCount < 5)) {
                        console.log(`Retrying ${!retryCount ? 1 : retryCount + 1}`);
                        return await this.connect(wifiPrinter, !retryCount ? 1 : retryCount + 1, silent, saveConnection);
                    } else {
                        if (!silent) {
                            this.toastService.error(`Could not connect to printer at ${wifiPrinter.ipAddress}.`);
                        }
                        this.javascriptLogService.logAction('epson-printer.service', 'connect', JSON.stringify(data), false);
                        resolve(false);
                    }
                }, {
                    eposprint: true
                });
            } catch (error) {
                if (!silent) {
                    this.loadingService.dismiss();
                }
                this.javascriptLogService.logAction('epson-printer.service', 'connect', JSON.stringify(error), false);
                resolve(false);
            }
        });
    }

    async delay() {
        return new Promise((resolve, reject) => {
            setTimeout(_ => resolve(true), 1000);
        });
    }

    disconnect() {
        this.wifiPrinterService.setCurrent(null);
        this.connectedSubject.next(null);
        this.ePosDevice.disconnect();
    }

    async openDrawer() {
        if (!!this.currentWifiPrinter) {
            const printerDevice = await this.getPrinterDevice() as any;
            if (!!printerDevice) {
                try {
                    printerDevice.addPulse();
                    printerDevice.send(this.randomId());
                } catch (error) {
                    this.javascriptLogService.logAction('epson-printer.service', 'openDrawer', JSON.stringify(error), false);
                    this.connect(this.currentWifiPrinter, 0, true).then(c => {
                        if (c) {
                            this.openDrawer();
                        }
                    });
                }
            }
        }
    }

    async print(msg: string): Promise<boolean> {
        return new Promise(async (resolve) => {
            if (!!this.currentWifiPrinter) {
                const printerDevice = await this.getPrinterDevice() as any;
                if (!!printerDevice) {
                    try {
                        try {
                            printerDevice.addTextAlign(printerDevice.ALIGN_CENTER);
                            printerDevice.addLogo(32, 32);
                            printerDevice.addTextAlign(printerDevice.ALIGN_LEFT);
                            printerDevice.addText(msg);
                            await this.send(printerDevice);
                            resolve(true);
                        } catch (error) {
                            console.log(error);
                            this.loadingService.dismiss();
                            this.toastService.error('Could not print receipt.');
                            resolve(false);
                        }
                    } catch (error) {
                        this.javascriptLogService.logAction('epson-printer.service', 'print', JSON.stringify(error), false);
                        this.toastService.error('Could not print receipt.');
                        resolve(false);
                    }
                }
            } else {
                this.toastService.error('No selected printer.');
                resolve(false);
            }
        });
    }

    async printGiftCardBalances(organization: Organization, giftCards: GiftCard[]) {
        if (!!this.currentWifiPrinter) {
            const printerDevice = await this.getPrinterDevice() as any;
            if (!!printerDevice) {
                try {
                    try {
                        giftCards.forEach(giftCard => {
                            printerDevice.addTextAlign(printerDevice.ALIGN_CENTER);
                            printerDevice.addLogo(32, 32);

                            printerDevice.addTextAlign(printerDevice.ALIGN_LEFT);
                            printerDevice.addText(buildGiftReceiptHeader(organization, this.currentWifiPrinter.columns));

                            printerDevice.addTextAlign(printerDevice.ALIGN_CENTER);
                            printerDevice.addTextSize(2, 1);
                            printerDevice.addText(`\n${giftCard.cardNumber}\n\n`);
                            printerDevice.addText(`\nBalance: ${this.currencyPipe.transform(giftCard.balance)}\n\n`);

                            this.send(printerDevice);
                        });
                    } catch (error) {
                        this.javascriptLogService.logAction('epson-printer.service', 'printGiftCardBalances', JSON.stringify(error), false);
                        this.toastService.error('Could not print gift card.');
                    }
                } catch (error) {
                    console.log(error);
                }
            }
        } else {
            this.toastService.error('No selected printer.');
        }
    }

    async printClaimTab(tab: Tab) {
        if (!!this.currentWifiPrinter) {
            const printerDevice = await this.getPrinterDevice() as any;
            if (!!printerDevice) {
                try {
                    printerDevice.addTextAlign(printerDevice.ALIGN_CENTER);
                    printerDevice.addLogo(32, 32);

                    printerDevice.addTextSize(2, 1);
                    printerDevice.addText(`\nThank you ${tab.personName}!\n\n`);
                    printerDevice.addTextSize(1, 1);
                    printerDevice.addText(`Scan the QR code to manage your tab from your phone.\n\n`);

                    printerDevice.addSymbol(`${this.config.mobileUrl}/#/claim-tab/${tab.id}/${tab.claimCode}`,
                        printerDevice.SYMBOL_QRCODE_MODEL_2, printerDevice.LEVEL_M, 8, 8, 0, 0, true);

                    if (!!this.currentOrganization && !!this.currentOrganization.claimTabReceiptTag) {
                        printerDevice.addText(`\n\n${this.currentOrganization.claimTabReceiptTag}\n\n`);
                    }

                    await this.send(printerDevice);
                } catch (error) {
                    this.javascriptLogService.logAction('epson-printer.service', 'printClaimTab', JSON.stringify(error), false);
                    this.toastService.error('Could not print claim.');
                }
            }
        } else {
            this.toastService.error('No selected printer.');
        }
    }

    async printClaimMembership(member: Membership) {
        if (!!this.currentWifiPrinter) {
            const printerDevice = await this.getPrinterDevice() as any;
            if (!!printerDevice) {
                try {
                    printerDevice.addTextAlign(printerDevice.ALIGN_CENTER);
                    printerDevice.addLogo(32, 32);

                    printerDevice.addTextSize(2, 1);
                    printerDevice.addText(`\nThank you ${!!member.person ? member.person.name : ''}!\n\n`);
                    printerDevice.addTextSize(1, 1);
                    printerDevice.addText(`Scan the QR code to manage your membership from your phone.\n\n`);

                    printerDevice.addSymbol(`${this.config.mobileUrl}/#/${member.orgId}/claim-membership/${member.id}?claimCode=${member.claimCode}`,
                        printerDevice.SYMBOL_QRCODE_MODEL_2, printerDevice.LEVEL_M, 8, 8, 0, 0, true);

                    await this.send(printerDevice);
                } catch (error) {
                    this.javascriptLogService.logAction('epson-printer.service', 'printClaimMembership', JSON.stringify(error), false);
                    this.toastService.error('Could not print claim.');
                }
            }
        } else {
            this.toastService.error('No selected printer.');
        }
    }

    async printCashSession(cashSession: CashSession, summary: CashSessionSummary, organization: Organization) {
        if (!!this.currentWifiPrinter) {
            const printerDevice = await this.getPrinterDevice() as any;
            if (!!printerDevice) {
                try {
                    printerDevice.addTextSmooth(true);
                    printerDevice.addTextAlign(printerDevice.ALIGN_CENTER);
                    printerDevice.addText(organization.name + '\n\n');
                    printerDevice.addTextAlign(printerDevice.ALIGN_RIGHT);
                    printerDevice.addText(`${formattedDate(cashSession.pendedTime)}\n`);
                    this.addSeparator(printerDevice);
                    printerDevice.addTextAlign(printerDevice.ALIGN_LEFT);

                    printerDevice.addText('\nSales\n');
                    this.addSeparator(printerDevice);
                    printerDevice.addText(buildLineItem('Cash:',
                        this.currencyPipe.transform(summary.cashSalesNet), 0, this.currentWifiPrinter.columns));
                    printerDevice.addText(buildLineItem('Charge:',
                        this.currencyPipe.transform(summary.chargePrincipal), 0, this.currentWifiPrinter.columns));
                    printerDevice.addText(buildLineItem('Gift Card:',
                        this.currencyPipe.transform(summary.giftCardPrincipal), 0, this.currentWifiPrinter.columns));
                    printerDevice.addText(buildLineItem('Comps:',
                        this.currencyPipe.transform(summary.creditAmount), 0, this.currentWifiPrinter.columns));
                    printerDevice.addText(buildLineItem('Total:',
                        this.currencyPipe.transform(summary.total), 0, this.currentWifiPrinter.columns));
                    this.addSeparator(printerDevice);

                    printerDevice.addText('\nTips\n');
                    this.addSeparator(printerDevice);
                    printerDevice.addText(buildLineItem('Cash:',
                        this.currencyPipe.transform(summary.cashTipIn), 0, this.currentWifiPrinter.columns));
                    printerDevice.addText(buildLineItem('Charge:',
                        this.currencyPipe.transform(summary.chargeTip), 0, this.currentWifiPrinter.columns));
                    printerDevice.addText(buildLineItem('Gift Card:',
                        this.currencyPipe.transform(summary.giftCardTip), 0, this.currentWifiPrinter.columns));
                    printerDevice.addText(buildLineItem('Adjustments:',
                        this.currencyPipe.transform(summary.tipAdjustments), 0, this.currentWifiPrinter.columns));
                    printerDevice.addText(buildLineItem('Total:',
                        this.currencyPipe.transform(summary.tipTotal), 0, this.currentWifiPrinter.columns));
                    this.addSeparator(printerDevice);

                    printerDevice.addText(buildLineItem('Transfers:',
                        this.currencyPipe.transform(summary.transfers), 0, this.currentWifiPrinter.columns));
                    this.addSeparator(printerDevice);

                    if (!!cashSession.cashDrawers && cashSession.cashDrawers.length > 0) {
                        cashSession.cashDrawers.forEach(d => {
                            printerDevice.addText(`\n${d.name}\n`);
                            this.addSeparator(printerDevice);
                            printerDevice.addText(buildLineItem('Final Count:', this.currencyPipe.transform(d.endAmount),
                                0, this.currentWifiPrinter.columns));
                            if (d.ones) {
                                printerDevice.addText(buildLineItem('Ones:',
                                    d.ones.toString(10), 0, this.currentWifiPrinter.columns));
                            }
                            if (d.fives) {
                                printerDevice.addText(buildLineItem('Fives:',
                                    d.fives.toString(10), 0, this.currentWifiPrinter.columns));
                            }
                            if (d.tens) {
                                printerDevice.addText(buildLineItem('Tens:',
                                    d.tens.toString(10), 0, this.currentWifiPrinter.columns));
                            }
                            if (d.twenties) {
                                printerDevice.addText(buildLineItem('Twenties:',
                                    d.twenties.toString(10), 0, this.currentWifiPrinter.columns));
                            }
                            if (d.fifties) {
                                printerDevice.addText(buildLineItem('Fifties:',
                                    d.fifties.toString(10), 0, this.currentWifiPrinter.columns));
                            }
                            if (d.hundreds) {
                                printerDevice.addText(buildLineItem('Hundreds:',
                                    d.hundreds.toString(10), 0, this.currentWifiPrinter.columns));
                            }
                            if (d.pennies) {
                                printerDevice.addText(buildLineItem('Pennies:',
                                    d.pennies.toString(10), 0, this.currentWifiPrinter.columns));
                            }
                            if (d.nickels) {
                                printerDevice.addText(buildLineItem('Nickels:',
                                    d.nickels.toString(10), 0, this.currentWifiPrinter.columns));
                            }
                            if (d.dimes) {
                                printerDevice.addText(buildLineItem('Dimes:',
                                    d.dimes.toString(10), 0, this.currentWifiPrinter.columns));
                            }
                            if (d.quarters) {
                                printerDevice.addText(buildLineItem('Quarters:',
                                    d.quarters.toString(10), 0, this.currentWifiPrinter.columns));
                            }
                            this.addSeparator(printerDevice);
                        });
                    }

                    if (!!cashSession.notes) {
                        cashSession.notes.forEach(note => {
                            printerDevice.addText(buildLineItem(note.createdBy.name,
                                this.datePipe.transform(note.createDate), 0, this.currentWifiPrinter.columns));
                            printerDevice.addText(note.note + '\n', 0, this.currentWifiPrinter.columns);
                            this.addSeparator(printerDevice);
                        });
                    }
                    await this.send(printerDevice);
                } catch (error) {
                    this.javascriptLogService.logAction('epson-printer.service', 'printCashSession', JSON.stringify(error), false);
                    this.toastService.error('Could not print summary');
                }
            }
        } else {
            this.toastService.error('No selected printer.');
        }
    }

    async printTimeCard(timecard: Timecard, organization: Organization) {
        if (!!this.currentWifiPrinter) {
            const printerDevice = await this.getPrinterDevice() as any;
            if (!!printerDevice) {
                try {
                    printerDevice.addTextSmooth(true);
                    printerDevice.addTextAlign(printerDevice.ALIGN_LEFT);
                    printerDevice.addText(organization.name + '\n');
                    printerDevice.addText(timecard.person.name + '\n');
                    printerDevice.addTextAlign(printerDevice.ALIGN_LEFT);
                    this.addSeparator(printerDevice);
                    printerDevice.addText(buildLineItem('Clock In:',
                        formatDate(timecard.clockInTime, 'M/d/yy h:mm a', 'en-US'), 0, this.currentWifiPrinter.columns));

                    if (!!timecard.clockOutTime) {
                        printerDevice.addText(buildLineItem('Clock Out:',
                            formatDate(timecard.clockOutTime, 'M/d/yy h:mm a', 'en-US'), 0, this.currentWifiPrinter.columns));

                        printerDevice.addText(buildLineItem('Duration:',
                            formatToHoursAndMinutes(
                                moment.duration(+new Date(timecard.clockOutTime) - +new Date(timecard.clockInTime))),
                            0, this.currentWifiPrinter.columns));

                        if (!!timecard.cashTips) {
                            printerDevice.addText(buildLineItem('Cash Tips:',
                                this.currencyPipe.transform(timecard.cashTips), 0, this.currentWifiPrinter.columns));
                        }

                        if (!!timecard.payrollTips) {
                            printerDevice.addText(buildLineItem('Payroll Tips:',
                                this.currencyPipe.transform(timecard.payrollTips), 0, this.currentWifiPrinter.columns));
                        }
                    }

                    await this.send(printerDevice);
                } catch (error) {
                    this.javascriptLogService.logAction('epson-printer.service', 'printTimeCard', JSON.stringify(error), false);
                    this.toastService.error('Could not print timecard.');
                }
            }
        } else {
            this.toastService.error('No selected printer.');
        }
    }

    async printOrder(order: Order, items: OrderItem[], wifiPrinter: WifiPrinter, silent = true) {
        return new Promise(async (resolve) => {
            if (!wifiPrinter || (!!this.currentWifiPrinter && wifiPrinter.id === this.currentWifiPrinter.id)) {
                if (!!this.currentWifiPrinter) {
                    const printerDevice = await this.getPrinterDevice() as any;
                    if (!!printerDevice) {
                        try {
                            await this.orderDetails(printerDevice, this.currentWifiPrinter, order, items);
                            resolve(true);
                        } catch (error) {
                            await this.toastService.error('Could not print order.');
                            console.log(error);
                            resolve(false);
                        }
                    } else {
                        resolve(false);
                    }
                } else {
                    await this.toastService.error('No selected printer.');
                    resolve(false);
                }
            } else {
                const usePrinter = !!wifiPrinter ? wifiPrinter : this.currentWifiPrinter;
                if (!!usePrinter) {
                    if (!silent) {
                        this.loadingService.present('Printing order.');
                    }
                    this.ePosDevice.connect(usePrinter.ipAddress, 8008, (data) => {
                        if (data === 'OK') {
                            this.ePosDevice.createDevice(
                                'local_printer',
                                this.ePosDevice.DEVICE_TYPE_PRINTER,
                                {crypto: true, buffer: false},
                                (printerDevice, retcode) => {
                                    if (retcode === 'OK' || retcode === 'SSL_CONNECT_OK') {
                                        try {
                                            if (!!printerDevice) {
                                                this.orderDetails(printerDevice, usePrinter, order, items);
                                                resolve(true);
                                            } else {
                                                if (!silent) {
                                                    this.loadingService.dismiss();
                                                    this.toastService.error('No printer is connected.');
                                                }
                                                resolve(false);
                                            }
                                        } catch (error) {
                                            this.javascriptLogService.logAction('epson-printer.service',
                                                'printOrder', JSON.stringify(error), false);
                                            if (!silent) {
                                                this.loadingService.dismiss();
                                                this.toastService.error('Could not print order');
                                            }
                                            resolve(false);
                                        }
                                        if (!silent) {
                                            this.loadingService.dismiss();
                                        }
                                    } else {
                                        if (!silent) {
                                            this.toastService.error(`Could not connect to printer at ${usePrinter.ipAddress}.`);
                                        }
                                        resolve(false);
                                    }
                                    if (!silent) {
                                        this.loadingService.dismiss();
                                    }
                                }
                            );
                        } else {
                            if (!silent) {
                                this.toastService.error(`Could not connect to printer at ${usePrinter.ipAddress}.`);
                                this.loadingService.dismiss();
                            }
                            resolve(false);
                        }
                    }, error => {
                        this.javascriptLogService.logAction('epson-printer.service', 'printOrder_2', JSON.stringify(error), false);
                        if (!silent) {
                            this.toastService.error('Could not print order.');
                            this.loadingService.dismiss();
                        }
                        resolve(false);
                    });
                } else {
                    await this.toastService.error('No selected printer.');
                    resolve(false);
                }
            }
        });
    }

    async orderDetails(printerDevice: any, usePrinter: any, order: Order, items: OrderItem[]) {
        printerDevice.addTextSmooth(true);
        printerDevice.addTextAlign(printerDevice.ALIGN_CENTER);

        printerDevice.addText('\n\n');

        if (!!order.orderMarker) {
            printerDevice.addTextSize(5, 6);
            printerDevice.addText(order.orderMarker + '\n\n');
            printerDevice.addTextSize(1, 1);
            this.addSeparator(printerDevice, usePrinter);
        } else if (!!order.orderNumber) {
            printerDevice.addTextSize(5, 6);
            printerDevice.addText(order.orderNumber + '\n\n');
            printerDevice.addTextSize(1, 1);
            this.addSeparator(printerDevice, usePrinter);
        }

        if (!!order.tableStandMarker) {
            printerDevice.addTextSize(5, 6);
            printerDevice.addText(order.tableStandMarker + '\n\n');
            printerDevice.addTextSize(1, 1);
            this.addSeparator(printerDevice, usePrinter);
        }

        printerDevice.addTextAlign(printerDevice.ALIGN_LEFT);

        if (!!order.notes) {
            printerDevice.addText('\n' + order.notes + '\n\n');
            this.addSeparator(printerDevice, usePrinter);
        }

        if (!!order.pickupLocation) {
            printerDevice.addText(order.pickupLocation.name + '\n');
            this.addSeparator(printerDevice, usePrinter);
        }

        if (!!order.submittedBy) {
            printerDevice.addTextSize(1, 1);
            printerDevice.addText('Server: ' + order.submittedBy.name + '\n');
            this.addSeparator(printerDevice, usePrinter);
        }

        if (!!order.customerAreaPosition) {
            printerDevice.addTextSize(1, 1);
            printerDevice.addText(order.customerAreaPosition.formattedValue() + '\n');
            this.addSeparator(printerDevice, usePrinter);
        }

        if (!!order.partySize) {
            printerDevice.addTextSize(1, 1);
            printerDevice.addText(order.partySize + ' Guest' + (order.partySize > 1 ? 's' : '') + '\n');
            this.addSeparator(printerDevice, usePrinter);
        }

        if (!!order.tabPersonName) {
            printerDevice.addTextSize(2, 2);
            printerDevice.addText(order.tabPersonName + '\n');
            printerDevice.addTextSize(1, 1);
            this.addSeparator(printerDevice, usePrinter);
        }

        printerDevice.addTextAlign(printerDevice.ALIGN_LEFT);
        items.forEach(item => {
            printerDevice.addTextSize(2, 2);
            if (item.orderType === Availabilities.TOGO) {
                printerDevice.addText('TOGO\n');
            }
            let msg = !!item.menuItem ? item.menuItem.name : 'Misc';
            if (!!item.selectedPrice && item.selectedPrice.name !== 'Default') {
                msg += ` (${item.selectedPrice.name})`;
            }
            printerDevice.addText(msg + '\n');

            this.addItemDetails(printerDevice, item);

            printerDevice.addText('\n');

            this.addSeparator(printerDevice, usePrinter);
        });

        printerDevice.addTextAlign(printerDevice.ALIGN_RIGHT);
        printerDevice.addText(formattedDate(order.date) + '\n');

        await this.send(printerDevice);
    }

    addItemDetails(printerDevice: any, item: OrderItem, additionalPadding = '') {
        printerDevice.addTextSize(2, 2);

        if (!!item.groupedModifiers) {
            item.groupedModifiers.forEach(g => {
                g.modifiers.forEach(m => {
                    printerDevice.addText(`${additionalPadding}  ${m.menuModifier.name}\n`);
                });
            });
        }

        if (item.displayIncludedAndModifiedToppingsFilter().length > 0) {
            item.displayIncludedAndModifiedToppingsFilter().forEach(t => {
                printerDevice.addText(`${additionalPadding}  ${!!t.menuItemTopping ? t.menuItemTopping.name : t.name}\n`);

                if (!!t.groupedModifiers) {
                    t.groupedModifiers.forEach(g => {
                        g.modifiers.forEach(m => {
                            printerDevice.addText(`${additionalPadding}    ${m.menuModifier.name}\n`);
                        });
                    });
                }
            });
        }

        if (item.removedToppingsFilter().length > 0) {
            printerDevice.addText(`${additionalPadding}  Remove:\n`);
            item.removedToppingsFilter().forEach(t => {
                printerDevice.addText(`${additionalPadding}    ${!!t.menuItemTopping ? t.menuItemTopping.name : t.name}\n`);
            });
        }

        if (item.displayAddedToppingsFilter().length > 0) {
            printerDevice.addText(`${additionalPadding}  Add:\n`);
            item.displayAddedToppingsFilter().forEach(t => {
                printerDevice.addText(`${additionalPadding}    ${!!t.menuItemTopping ? t.menuItemTopping.name : t.name}\n`);

                if (!!t.groupedModifiers) {
                    t.groupedModifiers.forEach(g => {
                        g.modifiers.forEach(m => {
                            printerDevice.addText(`${additionalPadding}    ${m.menuModifier.name}\n`);
                        });
                    });
                }
            });
        }

        if (!!item.groupedToppings && item.groupedToppings.length > 0) {
            item.groupedToppings.forEach(g => {
                printerDevice.addText(`${additionalPadding}  ${g.name}\n`);
                g.toppings.forEach(t => {
                    printerDevice.addText(`${additionalPadding}    ${!!t.menuItemTopping ? t.menuItemTopping.name : t.name}\n`);
                    if (!!t.groupedModifiers) {
                        t.groupedModifiers.forEach(toppingModifierGroup => {
                            toppingModifierGroup.modifiers.forEach(m => {
                                if (m.menuModifier.name !== g.name) {
                                    printerDevice.addText(`${additionalPadding}      ${m.menuModifier.name}\n`);
                                }
                            });
                        });
                    }
                });
            });
        }

        const displaySelections = !!item.selections && item.selections.length > 0 ?
            item.selections.filter(selection => {
                if (!!selection.menuItemSelection.menuSelection.defaultOptions
                    && selection.menuItemSelection.menuSelection.defaultOptions.length > 0
                    && (!selection.selectedItems || selection.selectedItems.length === 0)) {
                    return true;
                }

                if (selection.countTotal() > 0) {
                    return true;
                }

                if (!!selection.menuItemSelection.menuSelection.defaultOptions) {
                    for (const defaultItem of selection.menuItemSelection.menuSelection.defaultOptions) {
                        if (!defaultItem.printForDefault) {
                            if (!selection.selectedItems.some(i => i.menuSelectionItem.id === defaultItem.id)) {
                                return true;
                            }
                        }
                    }
                }
                for (const selectedItem of selection.selectedItems) {
                    if (!selectedItem.menuSelectionItem.defaultSelection || selectedItem.menuSelectionItem.printForDefault) {
                        return true;
                    }
                }

                return false;
            }) : [];

        if (!!displaySelections && displaySelections.length > 0) {
            displaySelections.forEach(selection => {
                if (!!selection.menuItemSelection.menuSelection.defaultOptions) {
                    for (const defaultItem of selection.menuItemSelection.menuSelection.defaultOptions) {
                        if (!defaultItem.printForDefault) {
                            if (!selection.selectedItems.some(i => i.menuSelectionItem.id === defaultItem.id)) {
                                printerDevice.addTextSize(2, 2);
                                printerDevice.addText(`${additionalPadding}    Remove: ${defaultItem.name}\n`);
                            }
                        }
                    }
                }

                for (const selectedItem of selection.selectedItems) {
                    if (!selectedItem.menuSelectionItem.defaultSelection || selectedItem.menuSelectionItem.printForDefault) {
                        printerDevice.addTextSize(2, 2);
                        printerDevice.addText(`${additionalPadding}  ${selectedItem.menuSelectionItem.name}\n`);
                    }
                }
            });
        }

        if (!!item.notes) {
            printerDevice.addTextSize(2, 2);
            printerDevice.addText(`\n\n${additionalPadding}  Notes:\n`);
            printerDevice.addText(`${additionalPadding}  ${item.notes}\n`);
        }
        printerDevice.addTextSize(1, 1);
    }

    addSeparator(printerDevice, wifiPrinter = null) {
        printerDevice.addTextSize(1, 1);
        printerDevice.addText(lineSeparator(!!wifiPrinter ? wifiPrinter.columns : (!!this.currentWifiPrinter
            ? this.currentWifiPrinter.columns
            : 35)));
    }

    async send(printerDevice) {
        try {
            printerDevice.addCut();
            printerDevice.onreceive = (res) => {
                this.loadingService.dismiss();
                return Promise.resolve('success');
            };
            printerDevice.onerror = (err) => {
                if (!!err && !!err.status && err.status !== '0') {
                    this.toastService.error(err.status);
                }
                this.loadingService.dismiss();

                return Promise.resolve(err.status);
            };
            printerDevice.send(this.randomId());
        } catch (e) {
            try {
                const c = await this.connect(this.currentWifiPrinter, 0, true);
                if (c) {
                    return this.send(printerDevice);
                } else {
                    return Promise.resolve('failure');
                }
            } catch (err) {
                return Promise.resolve(!!err ? err.status : 'failure');
            }
        }
    }

    pulse() {
        if (!!this.currentWifiPrinter && !!this.printerDevice && this.ePosDevice.isConnected()) {
            try {
                this.printerDevice.send(this.randomId());
            } catch (error) {
                if (this.failedRetry < 3) {
                    console.log('Connection lost attempting reconnect');
                    this.connect(this.currentWifiPrinter, 0, true).then(c => {
                        if (c) {
                            this.failedRetry = 0;
                        } else {
                            this.failedRetry++;
                        }
                    }, () => this.failedRetry++);
                }
            }
        } else if (!!this.currentWifiPrinter && !this.ePosDevice.isConnected()) {
            this.connect(this.currentWifiPrinter, 0, true);
        }
    }

    async getPrinterDevice() {
        return new Promise((resolve) => {
            try {
                if (this.printerDevice && this.ePosDevice.isConnected()) {
                    resolve(this.printerDevice);
                } else if (!!this.ePosDevice && !!this.currentWifiPrinter) {
                    console.log('Reconnecting printer.');
                    this.connect(this.currentWifiPrinter, 0, true).then((c) => {
                        if (c === true) {
                            this.ePosDevice.createDevice(
                                'local_printer',
                                this.ePosDevice.DEVICE_TYPE_PRINTER,
                                {crypto: true, buffer: false},
                                (printerDevice, retcode) => {
                                    if (retcode === 'OK' || retcode === 'SSL_CONNECT_OK') {
                                        this.printerDevice = printerDevice;
                                        resolve(this.printerDevice);
                                    } else {
                                        this.printerDevice = null;
                                        resolve(null);
                                    }
                                }
                            );
                        } else {
                            this.printerDevice = null;
                            resolve(null);
                        }
                    });
                } else if (!!this.currentWifiPrinter) {
                    this.ePosDevice.createDevice(
                        'local_printer',
                        this.ePosDevice.DEVICE_TYPE_PRINTER,
                        {crypto: true, buffer: false},
                        (printerDevice, retcode) => {
                            if (retcode === 'OK' || retcode === 'SSL_CONNECT_OK') {
                                this.printerDevice = printerDevice;
                                resolve(this.printerDevice);
                            } else {
                                this.printerDevice = null;
                                resolve(null);
                            }
                        }
                    );
                } else {
                    resolve(null);
                }
            } catch (error) {
                resolve(null);
            }
        });
    }

    randomId() {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const charactersLength = characters.length;
        let counter = 0;
        while (counter < 6) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
            counter += 1;
        }
        return result;
    }
}
